import React, { useState, useEffect } from 'react';
import { 
  createBrowserRouter, 
  RouterProvider, 
  createRoutesFromElements, 
  Route, 
  Navigate,
  Outlet
} from 'react-router-dom';
import axios from 'axios';
import { useStateContext } from './contexts/ContextProvider';

import { Footer, Sidebar, ThemeSettings } from './components';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import Student from './pages/Student';
import ViewStudent from './pages/StudentView';
import LiveClasses from './pages/LiveClasses';
import Batches from './pages/Batches'; 
import BatchView from './pages/BatchView';
import BatchLive from './pages/BatchLive';
import Users from './pages/Users';
import Folders from './pages/Folders';
import TestView from './pages/TestView';
import TestCreate from './pages/TestCreate';
import TestQuestionsView from './pages/TestQuestionView';
import AppHomeManagement from './pages/AppHome';
import StudentActionsTable from './pages/StudentActions';
import ProgramDetails from './pages/ProgramDetails';
import StudentCallbacks from './pages/StudentCallbacks';
import OrdersDashboard from './pages/OrdersDashboard';
import BatchTest from './pages/BatchTest';
import BatchTestResults from './pages/BatchTestResults';
import LocationMap from './pages/LocationMap'; 
import StudentManagementDashboard from './pages/StudentCrm';
import StudentLead from './pages/Studentlead'; 
import StudentTestRecords from './pages/StudentTestRecords';
import TestSeries from './pages/TestSeries';
import RecordedCourse from './pages/RecordedCourse';
import TestSeriesContent from './pages/TestSeriesContent';
import RecordedCourseContent from './pages/RecordedCourseContent';
import CommunityDocuments from './pages/CommunityDocuments';
import DisscussAsk from './pages/DiscussAsk';
import TestOfDayManager from './pages/Testoftheday';
import StudentDashboard from './pages/Student/StudentDashboard';
import ScholarshipTestRecords from './pages/ScholarshipTestRecords';
import TeacherBatches from './pages/TeacherBatches';
import TestOfDayResults from './pages/TestOfthedayResult';
// Layout component to wrap authenticated routes
const AdminLayout = ({ userRole, onLogout }) => {
  const {
    activeMenu,
    themeSettings,
    setThemeSettings,
    currentColor,
    currentMode,
  } = useStateContext();

  return (
    <div className={currentMode === 'Dark' ? 'dark' : ''}>
      <div className="flex relative dark:bg-main-dark-bg">
        {activeMenu ? (
          <div className="w-72 fixed sidebar dark:bg-secondary-dark-bg bg-white">
            <Sidebar role={userRole} />
          </div>
        ) : (
          <div className="w-0 dark:bg-secondary-dark-bg">
            <Sidebar role={userRole} />
          </div>
        )}
        <div
          className={`dark:bg-main-dark-bg bg-main-bg min-h-screen w-full ${
            activeMenu ? 'md:ml-72' : 'flex-2'
          }`}
        >
          <div>
            {themeSettings && <ThemeSettings />}
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

const App = () => {
  const { currentMode } = useStateContext();
  const [userRole, setUserRole] = useState(localStorage.getItem('role') || '');
  const [isLoggedIn, setIsLoggedIn] = useState(!!userRole);

  useEffect(() => {
    if (userRole) {
      setIsLoggedIn(true);
    } else {
      setIsLoggedIn(false);
    }
  }, [userRole]);

  // Logout function
  const handleLogout = () => {
    localStorage.removeItem('role');
    localStorage.removeItem('name');
    localStorage.removeItem('email');
    localStorage.removeItem('user_id');
    localStorage.removeItem('student_id');
    setUserRole('');
    setIsLoggedIn(false);
    window.location.href = '/';
  };

  // Data loaders
  const dashboardLoader = async () => {
    return { message: "Dashboard data loaded successfully" };
  };

  const studentLoader = async () => {
    return { students: [] };
  };

  const studentViewLoader = async ({ params }) => {
    try {
      const response = await axios.get(`https://apissc.sainikschoolcadet.com/api/Students/getbyid/${params.studentId}`);
      return response.data;
    } catch (err) {
      console.error('Error fetching student data:', err);
      throw new Error('Failed to fetch student data');
    }
  };
  
  const BatchTestLoader = async ({ params }) => {
    try {
      const response = await axios.get(`https://apissc.sainikschoolcadet.com/api/Batches/${params.batchId}`);
      return response.data;
    } catch (err) {
      console.error('Error fetching batch data for tests:', err);
      throw new Error('Failed to fetch batch data for tests');
    }
  };
  
  const BatchesLoader = async ({ params }) => {
    try {
      const response = await axios.get(`https://apissc.sainikschoolcadet.com/api/Contents/${params.contentId}`);
      return response.data;
    } catch (err) {
      console.error('Error fetching class data:', err);
      throw new Error('Failed to fetch class data');
    }
  };

  const BatchViewLoader = async ({ params }) => {
    try {
      const response = await axios.get(`https://apissc.sainikschoolcadet.com/api/Batches/${params.batchId}`);
      return response.data;
    } catch (err) {
      console.error('Error fetching batch data:', err);
      throw new Error('Failed to fetch batch data');
    }
  };

  const BatchLiveLoader = async ({ params }) => {
    try {
      const response = await axios.get(`https://apissc.sainikschoolcadet.com/api/Batches/${params.batchId}`);
      return response.data;
    } catch (err) {
      console.error('Error fetching batch data for live content:', err);
      throw new Error('Failed to fetch batch data for live content');
    }
  };

  const usersLoader = async () => {
    try {
      const response = await axios.get('https://apissc.sainikschoolcadet.com/api/users/showUsers');
      return { users: response.data };
    } catch (err) {
      console.error('Error fetching users data:', err);
      throw new Error('Failed to fetch users data');
    }
  };

  // Create routes based on authentication status
  let router;
  
  if (isLoggedIn) {
    // Check if user is a student
    if (userRole === 'student') {
      // Student-specific router
      router = createBrowserRouter(
        createRoutesFromElements(
          <>
            <Route path="/student-dashboard" element={<StudentDashboard />} />
            <Route path="/" element={<Navigate to="/student-dashboard" />} />
            <Route path="/login" element={<Navigate to="/student-dashboard" />} />
            <Route path="*" element={<Navigate to="/student-dashboard" />} />
          </>
        )
      );
    } else {
      // Admin, teacher, counselor routes (with common routes)
      const commonRoutes = () => {
        const routes = [];
        
        // Test Library and Folder Management
        routes.push(<Route key="folders" path="/folders" element={<Folders />} />);
        routes.push(<Route key="folder-detail" path="/folders/:folderId" element={<Folders />} />);
        routes.push(<Route key="nested-folder" path="/folders/:path+" element={<Folders />} />);
        
        // Test Management Routes
        routes.push(<Route key="test-view" path="/tests/:testId" element={<TestView />} />);
        routes.push(<Route key="test-view-alias" path="/test-view/:testId" element={<TestView />} />);
        routes.push(<Route key="test-create" path="/tests/create" element={<TestCreate />} />);
        routes.push(<Route key="test-questions-view" path="/questions/test/:testId" element={<TestQuestionsView />} />);
        routes.push(<Route key="test-add-questions" path="/tests/:testId/add-questions" element={
          <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">Add Questions Page</div>
        } />);
        routes.push(<Route key="question-view-edit" path="/questions/:questionId" element={
          <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">Question Details/Edit Page</div>
        } />);
        routes.push(<Route key="question-edit" path="/questions/:questionId/edit" element={
          <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">Edit Question Page</div>
        } />);
        
        // Student Management
        routes.push(<Route key="student" path="/student" element={<Student />} loader={studentLoader} />);
        routes.push(<Route key="view-student" path="/view-student/:studentId" element={<ViewStudent />} loader={studentViewLoader} />);
        routes.push(<Route key="student-actions" path="/student-actions" element={<StudentActionsTable />} />);
        routes.push(<Route key="student-test-records" path="/student-test-records" element={
          <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
            <StudentTestRecords />
          </div>
        } />);
        routes.push(<Route key="student-callbacks" path="/student-callbacks" element={<StudentCallbacks />} />);
        
        // Batch Management
        routes.push(<Route key="batch-view" path="/batches/:contentId" element={<Batches />} loader={BatchesLoader} />);
        routes.push(<Route key="batch-detail-view" path="/BatchView/:batchId" element={<BatchView />} loader={BatchViewLoader} />);
        routes.push(<Route key="batch-live-view" path="/livebatch/:batchId" element={<BatchLive />} loader={BatchLiveLoader} />);
        routes.push(<Route key="batch-test" path="/batchtest/:batchId" element={<BatchTest />} loader={BatchTestLoader} />);
        routes.push(<Route key="batch-test-results" path="/batch-test-results/:batchId/:testId" element={<BatchTestResults />} />);
        
        // Content Management
        routes.push(<Route key="live-classes" path="/live-classes" element={<LiveClasses />} />);
        routes.push(<Route key="recorded-course" path="/recorded-course" element={<RecordedCourse />} />);
        routes.push(<Route key="recorded-content-detail" path="/recorded-content/:contentId" element={<RecordedCourseContent />} />);
        routes.push(<Route key="test-series" path="/test-series" element={<TestSeries />} />);
        routes.push(<Route key="test-series-content" path="/test-series-content/:testId" element={<TestSeriesContent />} />);
        
        // Community Features
        routes.push(<Route key="community-documents" path="/community-documents" element={
          <div className="m-2 md:m-10 mt-24 bg-white rounded-3xl">
            <CommunityDocuments />
          </div>
        } />);
        routes.push(<Route key="discuss-ask" path="discuss-ask" element={<DisscussAsk />} />);
        routes.push(<Route key="forums" path="/forums" element={
          <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">Forums Page</div>
        } />);
        
        // Dashboard Management
        routes.push(<Route key="student-management" path="/student-management" element={
          <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
            <StudentManagementDashboard />
          </div>
        } />);
        routes.push(<Route key="orders" path="/orders" element={
          <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
            <OrdersDashboard />
          </div>
        } />);
        routes.push(<Route key="student-lead" path="/student-lead" element={
          <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
            <StudentLead />
          </div>
        } />);
        
        // Other Common Features
        routes.push(<Route key="testoftheday" path="/test-of-the-day" element={
          <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
            <TestOfDayManager />
          </div>
        } />);
        routes.push(<Route key="test-of-day-results" path="/test-of-day-results/:testId" element={
          <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
            <TestOfDayResults />
          </div>
        } />);
        routes.push(<Route key="student-locations" path="/student-locations" element={<LocationMap />} />);
        routes.push(<Route key="scholarship-tests" path="/scholarship-tests" element={
          <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
            <ScholarshipTestRecords />
          </div>
        } />);
        
        return routes;
      };
      
      // Admin-specific routes
      const adminSpecificRoutes = () => {
        const routes = [];
        
        routes.push(<Route key="dashboard" path="/" element={<Dashboard />} loader={dashboardLoader} />);
        routes.push(<Route key="users" path="/users" element={<Users />} loader={usersLoader} />);
        routes.push(<Route key="app-home-management" path="/app-home-management" element={<AppHomeManagement />} />);
        routes.push(<Route key="program-details" path="/program-details/:type" element={
          <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">
            <ProgramDetails />
          </div>
        } />);
        routes.push(<Route key="user" path="/user" element={
          <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">User Management Page</div>
        } />);
        routes.push(<Route key="groups" path="/groups" element={
          <div className="m-2 md:m-10 mt-24 p-2 md:p-10 bg-white rounded-3xl">Groups Page</div>
        } />);
        
        return routes;
      };
      
      // Teacher-specific routes
      const teacherSpecificRoutes = () => {
        const routes = [];
        
        routes.push(<Route key="my-batches" path="/my-batches" element={<TeacherBatches />} />);
        routes.push(<Route key="teacher-home" path="/" element={<Navigate to="/my-batches" />} />);
        
        return routes;
      };
      
      // Create router based on role
      if (userRole === 'teacher') {
        router = createBrowserRouter(
          createRoutesFromElements(
            <Route element={<AdminLayout userRole={userRole} onLogout={handleLogout} />}>
              {commonRoutes()}
              {teacherSpecificRoutes()}
              <Route path="/login" element={<Navigate to="/my-batches" />} />
              <Route path="*" element={<Navigate to="/my-batches" />} />
            </Route>
          )
        );
      } else if (userRole === 'admin') {
        router = createBrowserRouter(
          createRoutesFromElements(
            <Route element={<AdminLayout userRole={userRole} onLogout={handleLogout} />}>
              {commonRoutes()}
              {adminSpecificRoutes()}
              <Route path="/login" element={<Navigate to="/" />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Route>
          )
        );
      } else {
        // For other roles (like counselor)
        router = createBrowserRouter(
          createRoutesFromElements(
            <Route element={<AdminLayout userRole={userRole} onLogout={handleLogout} />}>
              {commonRoutes()}
              <Route key="dashboard" path="/" element={<Dashboard />} loader={dashboardLoader} />
              <Route path="/login" element={<Navigate to="/" />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Route>
          )
        );
      }
    }
  } else {
    // Create router for unauthenticated users
    router = createBrowserRouter(
      createRoutesFromElements(
        <>
          <Route path="/" element={<Login setUserRole={setUserRole} />} />
          <Route path="*" element={<Navigate to="/" />} />
        </>
      )
    );
  }

  return <RouterProvider router={router} />;
};

export default App;